import React, { useState, useEffect } from 'react';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import { useInternals } from '../../context/internalContext';
import FormattedInput from '../../components/FormatdInput/formatFunctions';
import { toast } from 'react-toastify';
import { useUsers } from '../../context/usersContext';
import useSubmitLock from '../../utils/useSubmitLock';

const ADDRESS_CHOICE_FIELDS = [
  'zone',
  'type_construction',
  'water_supply',
  'lighting_type',
  'sanitary_drainage',
  'trash',
];

const AddAddress = ({ onSaveSuccess, onCloseModal, internalId }) => {
  const { listCity, listState } = useAuxiliaries();
  const { createAddress } = useInternals();
  const { userInfo } = useUsers();

  const [states, setStates] = useState([]);
  const [stateSearchQuery, setStateSearchQuery] = useState('');
  const [filteredStates, setFilteredStates] = useState([]);
  const [shouldSearchState, setShouldSearchState] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [filteredCities, setFilteredCities] = useState([]);
  const [shouldSearchCity, setShouldSearchCity] = useState(false);

  const [selectedCityId, setSelectedCityId] = useState(null);

  const [address, setAddress] = useState({
    neighborhood: '',
    public_place: '',
    complement: '',
    cep: '',
    state: '',
    zone: '', 
    type_construction: '', 
    water_supply: '', 
    lighting_type: '', 
    sanitary_drainage: '', 
    trash: '', 
    person: '', 
    main: false
  });
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const normalizeText = (value) => {
    if (!value) return '';
    return value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const normalizeGroup = (value) => normalizeText(value).replace(/\s+/g, ' ').trim();
  const isSocialService = Array.isArray(userInfo?.groups)
    ? userInfo.groups.some((g) => normalizeGroup(g) === 'SERVICO SOCIAL')
    : false;

  useEffect(() => {
    async function fetchData() {
      const statesData = await listState();
      setStates(statesData?.results || []);
    }

    fetchData();
  }, [listState]);

  useEffect(() => {
    const query = stateSearchQuery.trim();

    if (!shouldSearchState || query.length < 2) {
      setFilteredStates([]);
      return;
    }

    const normalizedQuery = normalizeText(query);
    const filtered = states.filter((s) => {
      const name = normalizeText(s?.name || '');
      const uf = normalizeText(s?.uf || '');
      return name.includes(normalizedQuery) || uf.includes(normalizedQuery);
    });
    setFilteredStates(filtered);
  }, [stateSearchQuery, shouldSearchState, states]);

  useEffect(() => {
    const query = citySearchQuery.trim();

    if (!shouldSearchCity || query.length < 3) {
      setFilteredCities([]);
      return;
    }

    const debounce = setTimeout(async () => {
      try {
        const cityResponse = await listCity({ name: query });
        let cities = cityResponse?.results || [];

        if (address.state) {
          const normalizedState = normalizeText(address.state);
          cities = cities.filter((city) => {
            const stateName = normalizeText(city?.state_name || city?.state || city?.uf || city?.state_uf || '');
            return stateName.includes(normalizedState) || normalizedState.includes(stateName);
          });
        }

        setFilteredCities(cities);
      } catch (error) {
        console.error('Erro ao buscar cidades por nome:', error);
        setFilteredCities([]);
      }
    }, 350);

    return () => clearTimeout(debounce);
  }, [citySearchQuery, listCity, shouldSearchCity, address.state]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const normalizedFields = ['neighborhood', 'public_place', 'complement'];
    setAddress((prev) => ({
      ...prev,
      [name]: type === 'checkbox'
        ? checked
        : normalizedFields.includes(name)
          ? normalizeText(value)
          : value
    }));
  };

  const handleCitySearch = (query) => {
    setCitySearchQuery(query);
    setSelectedCityId(null);
    setShouldSearchCity(true);
  };

  const handleStateSearch = (query) => {
    setStateSearchQuery(query);
    setAddress((prev) => ({ ...prev, state: '' }));
    setSelectedCityId(null);
    setCitySearchQuery('');
    setFilteredCities([]);
    setShouldSearchState(true);
  };

  const selectState = (state) => {
    const stateValue = state?.name || state?.uf || '';
    setAddress((prev) => ({ ...prev, state: stateValue }));
    setStateSearchQuery(stateValue);
    setSelectedCityId(null);
    setCitySearchQuery('');
    setFilteredStates([]);
    setFilteredCities([]);
    setShouldSearchState(false);
  };

  const selectCity = (city) => {
    setSelectedCityId(city.id);
    setCitySearchQuery(city.name);
    setFilteredCities([]);
    setShouldSearchCity(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await runWithSubmitLock(async () => {
      // Validação: precisa ter interno selecionado
      if (!internalId) {
        toast.warning('Selecione um interno antes de cadastrar o endereço.');
        return;
      }
      if (!selectedCityId) {
        toast.warning('Selecione uma cidade na lista.');
        return;
      }
      if (!address.state) {
        toast.warning('Selecione um estado na lista.');
        return;
      }

      const formattedAddress = {
        ...address,
        city: selectedCityId,
        main: address.main === true,
        person: internalId
      };

      ADDRESS_CHOICE_FIELDS.forEach((field) => {
        if (!formattedAddress[field]) {
          delete formattedAddress[field];
        }
      });

      const createdAddress = await createAddress(formattedAddress);
      if (!createdAddress) {
        return;
      }

      onSaveSuccess();
      onCloseModal();
    });
  };

  return (
    <form onSubmit={handleSubmit} className="address-modal-form">
      <div className="modal-form-grid">
      <div className='form-group form-field field-half'>
        <label>Estado</label>
        <input
          type="text"
          name="state_search"
          value={stateSearchQuery}
          onChange={(e) => handleStateSearch(e.target.value)}
          placeholder="Digite o nome do estado"
          required
        />
        {filteredStates.length > 0 && (
          <ul className="suggestions-list-cities">
            {filteredStates.map((state) => (
              <li key={state.id || state.name} onClick={() => selectState(state)}>
                {state.name || state.uf}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='form-group form-field field-half'>
        <label>Cidade</label>
        <input
          type="text"
          name="city_search"
          value={citySearchQuery}
          onChange={(e) => handleCitySearch(e.target.value)}
          placeholder="Digite o nome da cidade"
          required
        />
        {filteredCities.length > 0 && (
          <ul className="suggestions-list-cities">
            {filteredCities.map((city) => (
              <li key={city.id} onClick={() => selectCity(city)}>
                {city.name}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='form-group field-half'>
        <label>Bairro</label>
        <input type="text" name="neighborhood" value={address.neighborhood} onChange={handleChange} required />
      </div>

      <div className='form-group field-half'>
        <label>CEP</label>
        <FormattedInput type="cep" name="cep" value={address.cep} onChange={handleChange} required />
      </div>

      <div className='form-group modal-form field-full'>
        <label>Logradouro</label>
        <input type="text" name="public_place" value={address.public_place} onChange={handleChange} required />
      </div>

      <div className='form-group modal-form field-half'>
        <label>Complemento</label>
        <input type="text" name="complement" value={address.complement} onChange={handleChange} />
      </div>

      {isSocialService && (
        <>
          <div className='form-group field-half'>
            <label>Zona</label>
            <select name="zone" value={address.zone} onChange={handleChange} required>
              <option value=''>Selecione</option>
              <option value='Rural'>Rural</option>
              <option value='Urbana'>Urbana</option>
            </select>
          </div>

          <div className='form-group field-half'>
            <label>Tipo de Construção</label>
            <select name="type_construction" value={address.type_construction} onChange={handleChange} required>
              <option value=''>Selecione</option>
              <option value='Alvenaria'>Alvenaria</option>
              <option value='Madeira'>Madeira</option>
              <option value='Palafita'>Palafita</option>
              <option value='Taipa'>Taipa</option>
            </select>
          </div>

          <div className='form-group field-half'>
            <label>Abastecimento de Água</label>
            <select name="water_supply" value={address.water_supply} onChange={handleChange} required>
              <option value=''>Selecione</option>
              <option value='Carro pipa'>Carro pipa</option>
              <option value='Poco'>Poço</option>
              <option value='Rede Publica'>Rede Pública</option>
            </select>
          </div>

          <div className='form-group field-half'>
            <label>Tipo de Iluminação</label>
            <select name="lighting_type" value={address.lighting_type} onChange={handleChange} required>
              <option value=''>Selecione</option>
              <option value='Medidor'>Medidor</option>
              <option value='Sem medidor'>Sem medidor</option>
            </select>
          </div>

          <div className='form-group field-half'>
            <label>Esgotamento Sanitário</label>
            <select name="sanitary_drainage" value={address.sanitary_drainage} onChange={handleChange} required>
              <option value=''>Selecione</option>
              <option value='Fossa'>Fossa</option>
              <option value='Rede Publica'>Rede Pública</option>
            </select>
          </div>

          <div className='form-group field-half'>
            <label>Descarte de Lixo</label>
            <select name="trash" value={address.trash} onChange={handleChange} required>
              <option value=''>Selecione</option>
              <option value='Coletado'>Coletado</option>
              <option value='Enterrado'>Enterrado</option>
              <option value='Queimado'>Queimado</option>
              <option value='Ceu Aberto'>Céu Aberto</option>
              <option value='Outros'>Outros</option>
            </select>
          </div>
        </>
      )}

      <div style={{ marginTop: '10px' }} className='form-group field-full'>
        <label>
          <input
            type="checkbox"
            name="main"
            checked={address.main}
            onChange={handleChange} />
          Endereço Principal
        </label>
      </div>
      <div className="field-full modal-form-actions">
        <button className='btn-edit' type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Adicionando...' : 'Adicionar'}
        </button>
      </div>
      </div>
    </form>
  );
};

export default AddAddress;
